import { hash } from '@ember/helper';
import { action } from '@ember/object';
import type Owner from '@ember/owner';
import { service } from '@ember/service';
import { cached, tracked } from '@glimmer/tracking';

import type { PopupData, Service } from 'webapp-types';
import { BillingFeature } from 'webapp/enums/enums';

import BillingDowngradeRowComponent from 'webapp/components/billing/downgrade-row';
import CommonUiIconComponent from 'webapp/components/common/ui-icon';
import type { Args } from 'webapp/components/popup/inner/modal';
import PopupInnerModalComponent from 'webapp/components/popup/inner/modal';
import SubmitButtonComponent from 'webapp/components/submit-button';

import type BillingPlan from 'webapp/models-custom/billing-plan';

export default class AccountBillingCancellationFlowPopupDowngradeLimitationsComponent extends PopupInnerModalComponent<PopupData.Billing.DowngradeLimitations> {
	@service('account') declare accountService: Service.Account;
	@service('billing') declare billingService: Service.Billing;
	@service('model-manager/boards') declare mmBoardsService: Service.ModelManager.Boards;
	@service('model-manager/custom-queries')
	declare mmCustomQueriesService: Service.ModelManager.CustomQueries;
	@service('model-manager/sources') declare mmSourcesService: Service.ModelManager.Sources;
	@service('model-manager/users') declare mmUsersService: Service.ModelManager.Users;
	@service('model-manager/spaces') declare mmSpacesService: Service.ModelManager.Spaces;
	@service('model-manager/scripts') declare mmScriptsService: Service.ModelManager.Scripts;
	@service('model-manager/reports') declare mmReportsService: Service.ModelManager.Reports;

	@tracked showSources = false;
	@tracked showDatasets = false;
	@tracked showSQLSources = false;
	@tracked showSheetSources = false;
	@tracked showBoards = false;
	@tracked showCustomMetrics = false;
	@tracked showClients = false;
	@tracked showUsers = false;
	@tracked showReports = false;

	constructor(owner: Owner, args: Args) {
		super(owner, args);

		// only show clients for agencies
		this.showClients =
			this.accountService.isAgency && this.mmSpacesService.otherSpaceRecords.length > 0;

		if (this.showClients) {
			this.showSources = false;
			this.showDatasets = false;
			this.showSQLSources = false;
			this.showSheetSources = false;
			this.showBoards = false;
			this.showUsers = false;
			this.showCustomMetrics = false;
			this.showReports = false;
		} else {
			this.showSources = this.mustDeleteSources;
			this.showDatasets = this.mustDeleteDatasets;
			this.showSQLSources = this.mustDeleteSQLSources;
			this.showSheetSources = this.mustDeleteSheetSources;
			this.showBoards = this.mustDeleteBoards;
			this.showUsers = this.mustDeleteUsers;
			this.showCustomMetrics = this.mustDeleteCustomMetrics;
			this.showReports = this.mustDeleteReports;

			void this.mmCustomQueriesService.requestCustomQueries();
			void this.mmScriptsService.requestScripts();
			void this.mmReportsService.requestReports();
		}
	}

	get targetPlan(): BillingPlan {
		return this.popupData.targetPlan;
	}

	get sources() {
		return this.mmSourcesService.sourceModels.filter((source) => {
			return !source.isRoot && !source.isDataset;
		});
	}

	get datasets() {
		return this.mmSourcesService.sourceModels.filter((source) => {
			return source.isDataset && !source.isSheets && !source.isTypeIngest;
		});
	}

	get sqlSources() {
		return this.mmSourcesService.sourceModels.filter((source) => {
			return source.isSqlConnector && !source.isDataset;
		});
	}

	get sheetSources() {
		return this.mmSourcesService.sourceModels.filter((source) => {
			return source.isSheets;
		});
	}

	get boards() {
		return this.mmBoardsService.normalBoardModels;
	}

	@cached
	get users() {
		const userRecords = this.mmUsersService.activatedUsersWithoutAgencyAdminsRecords;
		return this.mmUsersService.createModelsFromRecordsArray(userRecords);
	}

	@cached
	get customMetrics() {
		const customQueryRecords = this.mmCustomQueriesService.savedRecordsNotFromPublicTemplates,
			scriptRecords = this.mmScriptsService.savedRecords;
		return [
			...this.mmCustomQueriesService.createModelsFromRecordsArray(customQueryRecords),
			...this.mmScriptsService.createModelsFromRecordsArray(scriptRecords),
		];
	}

	get clients() {
		return this.mmSpacesService.otherSpaceModels;
	}

	get reports() {
		return this.mmReportsService.normalReportModels;
	}

	get boardLimit(): number | null {
		return this.targetPlan.features.boardsMax;
	}

	get mustDeleteSources(): boolean {
		return !this.billingService.checkForDowngradeLimitsForFeature(
			this.targetPlan,
			BillingFeature.SOURCES,
		);
	}

	get mustDeleteDatasets(): boolean {
		return !this.billingService.checkForDowngradeLimitsForFeature(
			this.targetPlan,
			BillingFeature.ADVANCED_DATA_SOURCES,
		);
	}

	get mustDeleteSQLSources(): boolean {
		return !this.billingService.checkForDowngradeLimitsForFeature(
			this.targetPlan,
			BillingFeature.SQL_SOURCES,
		);
	}

	get mustDeleteSheetSources(): boolean {
		return !this.billingService.checkForDowngradeLimitsForFeature(
			this.targetPlan,
			BillingFeature.SHEET_SOURCES,
		);
	}

	get mustDeleteBoards(): boolean {
		return !this.billingService.checkForDowngradeLimitsForFeature(
			this.targetPlan,
			BillingFeature.BOARDS,
		);
	}

	get mustDeleteUsers(): boolean {
		return !this.billingService.checkForDowngradeLimitsForFeature(
			this.targetPlan,
			BillingFeature.USERS,
		);
	}

	get mustDeleteCustomMetrics(): boolean {
		return !this.billingService.checkForDowngradeLimitsForFeature(
			this.targetPlan,
			BillingFeature.CUSTOM_METRICS,
		);
	}

	get mustDeleteReports(): boolean {
		return !this.billingService.checkForDowngradeLimitsForFeature(
			this.targetPlan,
			BillingFeature.REPORTS_BASIC,
		);
	}

	get mustDeleteClients(): boolean {
		return (
			this.showClients &&
			!this.billingService.checkForDowngradeLimitsForFeature(
				this.targetPlan,
				BillingFeature.CLIENTS,
			)
		);
	}

	get cannotDowngrade(): boolean {
		return (
			this.mustDeleteSources ||
			this.mustDeleteBoards ||
			this.mustDeleteUsers ||
			this.mustDeleteCustomMetrics ||
			this.mustDeleteDatasets ||
			this.mustDeleteSQLSources ||
			this.mustDeleteReports
		);
	}

	@action
	downgrade(): void {
		if (!this.cannotDowngrade) {
			this.args.model.triggerEvent('confirm');
		}
	}
	<template>
		<div class="content">
			<CommonUiIconComponent @name="DragAndDropArea" @size="xl" />
			<h2>
				One last step to prepare your
				{{this.targetPlan.name}}
				account
			</h2>

			{{#if this.showUsers}}
				<div class="section">
					<p>
						You have
						<strong>{{this.billingService.usersCurrent}} users</strong>, but the
						<strong>
							{{this.targetPlan.name}}
							plan is limited to
							{{this.targetPlan.features.usersMax~}}
						</strong>.
						<br />
						Please reduce the number of users to be able to downgrade:
					</p>
					<ul>
						{{#each this.users as |user|}}
							<BillingDowngradeRowComponent @model={{user}} @type="user" />
						{{/each}}
					</ul>
				</div>
			{{/if}}

			{{#if this.showSources}}
				<div class="section">
					<p>
						You have
						<strong>{{this.billingService.sourcesCurrent}} data sources</strong>, but
						the
						<strong>
							{{this.targetPlan.name}}
							plan is limited to
							{{this.targetPlan.features.sourcesMax~}}
						</strong>.
						<br />
						Please reduce the number of data sources to be able to downgrade:
					</p>
					<ul>
						{{#each this.sources as |source|}}
							<BillingDowngradeRowComponent @model={{source}} @type="source" />
						{{/each}}
					</ul>
				</div>
			{{/if}}

			{{#if this.showSQLSources}}
				<div class="section">
					<p>
						You have
						<strong>{{this.billingService.sqlSourcesCurrent}} SQL sources</strong>, but
						the
						<strong>
							{{this.targetPlan.name}}
							plan is limited to
							{{this.targetPlan.features.sqlSourcesMax~}}
						</strong>.
						<br />
						Please reduce the number of SQL sources to be able to downgrade:
					</p>
					<ul>
						{{#each this.sqlSources as |sqlSource|}}
							<BillingDowngradeRowComponent @model={{sqlSource}} @type="source" />
						{{/each}}
					</ul>
				</div>
			{{/if}}

			{{#if this.showDatasets}}
				<div class="section">
					<p>
						You have
						<strong>
							{{this.billingService.advancedDataSourcesCurrent}}
							datasets
						</strong>, but the
						<strong>
							{{this.targetPlan.name}}
							plan is limited to
							{{this.targetPlan.features.advancedDataSourcesMax~}}
						</strong>.
						<br />
						Please reduce the number of datasets to be able to downgrade:
					</p>
					<ul>
						{{#each this.datasets as |dataset|}}
							<BillingDowngradeRowComponent @model={{dataset}} @type="source" />
						{{/each}}
					</ul>
				</div>
			{{/if}}

			{{#if this.showSheetSources}}
				<div class="section">
					<p>
						You have
						<strong>
							{{this.billingService.sheetSourcesCurrent}}
							connected spreadsheets
						</strong>, but the
						<strong>
							{{this.targetPlan.name}}
							plan is limited to
							{{this.targetPlan.features.sheetSourcesMax~}}
						</strong>.
						<br />
						Please reduce the number of connected spreadsheets to be able to downgrade:
					</p>
					<ul>
						{{#each this.sheetSources as |source|}}
							<BillingDowngradeRowComponent @model={{source}} @type="source" />
						{{/each}}
					</ul>
				</div>
			{{/if}}

			{{#if this.showBoards}}
				<div class="section">
					<p>
						You have
						<strong>{{this.billingService.boardsCurrent}} dashboards</strong>, but the
						<strong>
							{{this.targetPlan.name}}
							plan is limited to
							{{this.boardLimit~}}
						</strong>.
						<br />
						Please reduce the number of dashboards to be able to downgrade:
					</p>
					<ul>
						{{#each this.boards as |board|}}
							<BillingDowngradeRowComponent @model={{board}} @type="board" />
						{{/each}}
					</ul>
				</div>
			{{/if}}

			{{#if this.showCustomMetrics}}
				<div class="section">
					<p>
						You have
						<strong>
							{{this.billingService.customMetricsCurrent}}
							custom metrics
						</strong>, but the
						<strong>
							{{this.targetPlan.name}}
							plan is limited to
							{{this.targetPlan.features.customMetricsMax~}}
						</strong>.
						<br />
						Please reduce the number of custom metrics to be able to downgrade:
					</p>
					<ul>
						{{#each this.customMetrics as |metric|}}
							<BillingDowngradeRowComponent @model={{metric}} @type="customMetric" />
						{{/each}}
					</ul>
				</div>
			{{/if}}

			{{#if this.showReports}}
				<div class="section">
					<p>
						You have
						<strong>{{this.reports.length}} reports</strong>, but the
						<strong>
							{{this.targetPlan.name}}
							plan is limited to
							{{this.targetPlan.features.reportsMax~}}
						</strong>.
						<br />
						Please reduce the number of reports to be able to downgrade:
					</p>
					<ul>
						{{#each this.reports as |report|}}
							<BillingDowngradeRowComponent @model={{report}} @type="report" />
						{{/each}}
					</ul>
				</div>
			{{/if}}

			{{#if this.showClients}}
				<div class="section">
					<p>
						{{#if this.mustDeleteClients}}
							You have
							<strong>{{this.billingService.clientsCurrent}} accounts</strong>, but
							the
							<strong>
								{{this.targetPlan.name}}
								plan is limited to
								{{this.targetPlan.features.clientsMax~}}
							</strong>.
							<br />
						{{/if}}
						{{#if this.mustDeleteUsers}}
							You have
							<strong>{{this.billingService.usersCurrent}} users</strong>, but the
							<strong>
								{{this.targetPlan.name}}
								plan is limited to
								{{this.targetPlan.features.usersMax~}}
							</strong>.
							<br />
						{{/if}}
						{{#if this.mustDeleteSources}}
							You have
							<strong>{{this.billingService.sourcesCurrent}} data sources</strong>,
							but the
							<strong>
								{{this.targetPlan.name}}
								plan is limited to
								{{this.targetPlan.features.sourcesMax~}}
							</strong>.
							<br />
						{{/if}}
						{{#if this.mustDeleteDatasets}}
							You have
							<strong>
								{{this.billingService.advancedDataSourcesCurrent}}
								datasets
							</strong>, but the
							<strong>
								{{this.targetPlan.name}}
								plan is limited to
								{{this.targetPlan.features.advancedDataSourcesMax~}}
							</strong>.
							<br />
						{{/if}}
						{{#if this.mustDeleteSQLSources}}
							You have
							<strong>{{this.billingService.sqlSourcesCurrent}} SQL sources</strong>,
							but the
							<strong>
								{{this.targetPlan.name}}
								plan is limited to
								{{this.targetPlan.features.sqlSourcesMax~}}
							</strong>.
							<br />
						{{/if}}
						{{#if this.mustDeleteSheetSources}}
							You have
							<strong>
								{{this.billingService.sheetSourcesCurrent}}
								connected spreadsheets
							</strong>, but the
							<strong>
								{{this.targetPlan.name}}
								plan is limited to
								{{this.targetPlan.features.sheetSourcesMax~}}
							</strong>.
							<br />
						{{/if}}
						{{#if this.mustDeleteBoards}}
							You have
							<strong>{{this.billingService.boardsCurrent}} dashboards</strong>, but
							the
							<strong>
								{{this.targetPlan.name}}
								plan is limited to
								{{this.boardLimit~}}
							</strong>.
							<br />
						{{/if}}
						{{#if this.mustDeleteCustomMetrics}}
							You have
							<strong>
								{{this.billingService.customMetricsCurrent}}
								custom metrics
							</strong>, but the
							<strong>
								{{this.targetPlan.name}}
								plan is limited to
								{{this.targetPlan.features.customMetricsMax~}}
							</strong>.
							<br />
						{{/if}}
						{{#if this.mustDeleteReports}}
							You have
							<strong>{{this.billingService.reportsCurrent}} reports</strong>, but the
							<strong>
								{{this.targetPlan.name}}
								plan is limited to
								{{this.targetPlan.features.reportsMax~}}
							</strong>.
							<br />
						{{/if}}

					</p>
					<ul>
						{{#each this.clients as |client|}}
							<BillingDowngradeRowComponent
								@model={{client}}
								@agencyLimitations={{hash
									mustDeleteClients=this.mustDeleteClients
									mustDeleteUsers=this.mustDeleteUsers
									mustDeleteSources=this.mustDeleteSources
									mustDeleteDatasets=this.mustDeleteDatasets
									mustDeleteBoards=this.mustDeleteBoards
									mustDeleteCustomMetrics=this.mustDeleteCustomMetrics
									mustDeleteReports=this.mustDeleteReports
								}}
								@type="client"
							/>
						{{/each}}
					</ul>
				</div>
			{{/if}}

			<div class="controls2 controls2--center">
				<SubmitButtonComponent
					@text="Downgrade plan"
					@size="big"
					@noDelay={{true}}
					@isDisabled={{this.cannotDowngrade}}
					@onTrigger={{this.downgrade}}
				/>

				<SubmitButtonComponent
					@text="I want to keep my subscription"
					@theme="grey-empty"
					@noDelay={{true}}
					@onTrigger={{this.close}}
				/>
			</div>

		</div>
	</template>
}
